import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, BarChart3, ClipboardCheck, ListChecks, School, Users, type LucideIcon } from "lucide-react";

import { attendanceService } from "../services/attendanceService";
import { usePermissions } from "../hooks/usePermissions";
import { todayIso } from "../utils/dates";
import { RegisterPanel } from "../components/attendance/RegisterPanel";
import { RecordsPanel } from "../components/attendance/RecordsPanel";
import { AlertsPanel, ClassesPanel, LearnersPanel } from "../components/attendance/FollowUpPanels";
import { PRESENTISH } from "../components/attendance/attendanceLogic";

type Tab = "register" | "records" | "learners" | "classes" | "alerts";
const PERIOD_CHOICES = [7, 30, 90];

/** Learner attendance: take the register, correct it, and follow up the learners and classes behind the numbers. */
export default function Attendance() {
  const { hasCapability } = usePermissions();
  const canCorrect = hasCapability("access.students.attendance_write");
  const tabs: { id: Tab; label: string; icon: LucideIcon }[] = [
    { id: "register", label: "Take the register", icon: ClipboardCheck },
    { id: "records", label: "Records", icon: ListChecks },
    { id: "learners", label: "Learners below 80%", icon: Users },
    { id: "classes", label: "Classes and days", icon: School },
    { id: "alerts", label: "Alerts", icon: AlertTriangle },
  ];
  const [params, setParams] = useSearchParams();
  const tab: Tab = tabs.some((item) => item.id === params.get("tab")) ? (params.get("tab") as Tab) : "register";
  const days = PERIOD_CHOICES.includes(Number(params.get("days"))) ? Number(params.get("days")) : 30;
  const select = (next: Tab, nextDays = days) => {
    const query: Record<string, string> = {};
    if (next !== "register") query.tab = next;
    if (nextDays !== 30) query.days = String(nextDays);
    setParams(query, { replace: true });
  };

  const queryClient = useQueryClient();
  const today = todayIso();
  const classes = useQuery({ queryKey: ["attendance", "classes"], queryFn: attendanceService.getRegisterClasses, staleTime: 10 * 60_000 });
  const todays = useQuery({ queryKey: ["attendance", "today", today], queryFn: () => attendanceService.getAttendanceRecords({ date: today, lesson: "day" }) });
  const analytics = useQuery({ queryKey: ["attendance", "analytics", days], queryFn: () => attendanceService.getAttendanceAnalytics(days), enabled: canCorrect });
  const alerts = useQuery({ queryKey: ["attendance", "alerts"], queryFn: () => attendanceService.getAttendanceAlerts({ include_resolved: "1" }) });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["attendance"] });

  const marks = todays.data ?? [];
  const classNames = new Set((classes.data ?? []).map((row) => row.name));
  const takenClasses = new Set(marks.map((row) => row.class_name).filter((name) => classNames.has(name))).size;
  const present = marks.filter((row) => PRESENTISH.includes(row.status)).length;
  const rate = marks.length ? (present / marks.length) * 100 : null;
  const low = analytics.data?.poor_attendance_students.length ?? 0;
  const openAlerts = (alerts.data ?? []).filter((row) => !row.is_resolved).length;
  const dash = (query: { isLoading: boolean }, text: string) => (query.isLoading ? "–" : text);

  const tiles: { label: string; value: string; note: string; go: () => void; icon: LucideIcon; alert?: boolean }[] = [
    { label: "Registers taken today", value: dash(classes, `${takenClasses} of ${classNames.size}`), note: takenClasses < classNames.size ? `${classNames.size - takenClasses} class${classNames.size - takenClasses === 1 ? "" : "es"} still to take` : "Every class is done", go: () => select("register"), icon: ClipboardCheck, alert: classNames.size > 0 && takenClasses < classNames.size },
    { label: "Present today", value: dash(todays, rate === null ? "—" : `${rate.toFixed(1)}%`), note: marks.length ? `${present} of ${marks.length} learners marked` : "No registers yet today", go: () => select("records"), icon: BarChart3, alert: rate !== null && rate < 80 },
    ...(canCorrect ? [{ label: `Below 80% (last ${days} days)`, value: dash(analytics, String(low)), note: low ? "Learners to look at" : "No learner below 80%", go: () => select("learners"), icon: Users, alert: low > 0 }] : []),
    { label: "Open alerts", value: dash(alerts, String(openAlerts)), note: openAlerts ? "To follow up" : "Nothing to follow up", go: () => select("alerts"), icon: AlertTriangle, alert: openAlerts > 0 },
  ];
  const badges: Partial<Record<Tab, number>> = { alerts: openAlerts, learners: low };
  const periodTab = tab === "learners" || tab === "classes";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Attendance</h1>
        <p className="mt-1 text-sm text-gray-500">Take the register for a day or a lesson, correct it, and follow up the learners and classes behind the numbers.</p>
      </div>

      <div className={`grid gap-3 sm:grid-cols-2 ${tiles.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
        {tiles.map((tile) => (
          <button
            key={tile.label}
            type="button"
            onClick={tile.go}
            className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tile.alert ? "bg-amber-50 text-amber-700" : "bg-primary-50 text-primary-700"}`}>
              <tile.icon className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-medium text-gray-500">{tile.label}</span>
              <span className={`block truncate text-xl font-semibold ${tile.alert ? "text-amber-800" : "text-gray-900"}`}>{tile.value}</span>
              <span className="block truncate text-xs text-gray-500">{tile.note}</span>
            </span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-gray-200">
        <nav role="tablist" aria-label="Attendance sections" className="-mb-px flex gap-x-6 overflow-x-auto">
          {tabs.filter((item) => canCorrect || (item.id !== "learners" && item.id !== "classes")).map((item) => {
            const active = tab === item.id;
            const badge = badges[item.id];
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => select(item.id)}
                className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium ${
                  active ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
                {badge ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs tabular-nums text-amber-800">{badge}</span> : null}
              </button>
            );
          })}
        </nav>
        {periodTab && (
          <div className="mb-2 flex items-center gap-1 text-xs" role="group" aria-label="Period">
            <span className="mr-1 font-medium text-gray-500">Last</span>
            {PERIOD_CHOICES.map((choice) => (
              <button key={choice} type="button" aria-pressed={days === choice} onClick={() => select(tab, choice)} className={`rounded-md px-2.5 py-1 font-medium ${days === choice ? "bg-primary-600 text-white" : "border border-gray-300 text-gray-700 hover:bg-gray-50"}`}>
                {choice} days
              </button>
            ))}
          </div>
        )}
      </div>

      {tab === "register" && <RegisterPanel onSaved={refresh} />}
      {tab === "records" && <RecordsPanel classes={classes.data ?? []} canCorrect={canCorrect} />}
      {tab === "learners" && canCorrect && (
        <LearnersPanel learners={analytics.data?.poor_attendance_students} days={days} isLoading={analytics.isLoading} isError={analytics.isError} onRetry={() => analytics.refetch()} />
      )}
      {tab === "classes" && canCorrect && <ClassesPanel analytics={analytics.data} isLoading={analytics.isLoading} isError={analytics.isError} onRetry={() => analytics.refetch()} />}
      {tab === "alerts" && <AlertsPanel alerts={alerts.data} isLoading={alerts.isLoading} isError={alerts.isError} onRetry={() => alerts.refetch()} onChanged={refresh} />}
    </div>
  );
}
